import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { rolesApi } from '@/services/api';
import type { Role } from '@/types/roles';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { Button as UiButton, Modal, RowActionMenu } from '@/components/ui';
import styles from './RolesManagement.module.css';

const RolesManagement: React.FC = () => {
  const navigate = useNavigate();

  const [roleRecords, setRoleRecords] = useState<Role[]>([]);
  const [isRoleListLoading, setIsRoleListLoading] = useState(true);

  const [rolePendingDeletion, setRolePendingDeletion] = useState<Role | null>(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const pageSize = 15;

  const fetchRoleRecords = useCallback(async () => {
    setIsRoleListLoading(true);
    try {
      setRoleRecords(await rolesApi.getRoles());
    } catch {
      toast.error('Failed to load role data');
    } finally {
      setIsRoleListLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoleRecords(); }, [fetchRoleRecords]);

  const handleEdit = useCallback((role: Role) => {
    navigate(`/roles/${role.id}/edit`);
  }, [navigate]);

  const handleDeleteRequest = useCallback((role: Role) => {
    setRolePendingDeletion(role);
  }, []);

  const confirmRoleDeletion = async () => {
    if (!rolePendingDeletion) return;

    setIsDeletingRole(true);
    try {
      await rolesApi.deleteRole(rolePendingDeletion.id);
      toast.success(`Role "${rolePendingDeletion.name}" deleted`);
      setRolePendingDeletion(null);
      await fetchRoleRecords();
    } catch (error: unknown) {
      const errorMessage = axios.isAxiosError(error) ? error.response?.data?.detail : 'Failed to delete role';
      toast.error(errorMessage ?? 'Failed to delete role');
    } finally {
      setIsDeletingRole(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const filteredAndSortedRoleRecords = useMemo(() => {
    const filtered = roleRecords.filter(r =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      let av: string | number = a.name.toLowerCase();
      let bv: string | number = b.name.toLowerCase();

      if (sortField === 'permissions') { av = a.scope_codenames.length; bv = b.scope_codenames.length; }
      else if (sortField === 'users') { av = a.user_count; bv = b.user_count; }

      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [roleRecords, searchQuery, sortField, sortDir]);

  const paginatedRoleRecords = filteredAndSortedRoleRecords.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const totalPages = Math.ceil(filteredAndSortedRoleRecords.length / pageSize) || 1;
  const totalCount = filteredAndSortedRoleRecords.length;

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const roleColumns = useMemo<ColumnDef<Role>[]>(() => [
    { key: 'name', header: 'Role Name', sortable: true, render: r => r.name },
    { key: 'permissions', header: 'Permissions', sortable: true, align: 'right', render: r => r.scope_codenames.length },
    { key: 'users', header: 'Users', sortable: true, align: 'right', render: r => r.user_count },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      width: '6%',
      render: (r) => {
        const canDelete = r.user_count === 0;
        return (
          <RowActionMenu
            label={`Actions for ${r.name}`}
            actions={[
              {
                label: 'Edit',
                icon: <EditIcon fontSize="small" />,
                onClick: () => handleEdit(r),
              },
              {
                label: (
                  <span title={!canDelete ? `Cannot delete — ${r.user_count} user(s) assigned` : undefined}>
                    Delete
                  </span>
                ),
                key: 'delete',
                icon: <DeleteIcon fontSize="small" />,
                tone: canDelete ? 'danger' : 'default',
                disabled: !canDelete,
                onClick: () => canDelete && handleDeleteRequest(r),
              },
            ]}
          />
        );
      },
    },
  ], [handleEdit, handleDeleteRequest]);

  return (
    <>
      <DataTable
        title="Roles Management"
        subtitle="Roles and the scopes each one grants."
        headerActions={
          <UiButton variant="primary" onClick={() => navigate('/roles/new')}>
            + New Role
          </UiButton>
        }
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search roles…"
        data={paginatedRoleRecords}
        columns={roleColumns}
        rowKey={r => r.id}
        totalCount={totalCount}
        loading={isRoleListLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        emptyMessage="No roles found."
      />

      <Modal
        open={Boolean(rolePendingDeletion)}
        title="Delete Role"
        onClose={() => setRolePendingDeletion(null)}
        isSubmitting={isDeletingRole}
        maxWidth={420}
        titleId="delete-role-title"
        // As before: a click outside closes it, Escape and a × do not.
        closeOnEscape={false}
        showClose={false}
        footer={
          <>
            <UiButton onClick={() => setRolePendingDeletion(null)} disabled={isDeletingRole}>
              Cancel
            </UiButton>
            <UiButton variant="danger" onClick={confirmRoleDeletion} disabled={isDeletingRole}>
              {isDeletingRole ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                  Deleting…
                </>
              ) : 'Delete'}
            </UiButton>
          </>
        }
      >
        {rolePendingDeletion && (
          <p className={styles['confirm-message']}>
            Are you sure you want to delete <strong>{rolePendingDeletion.name}</strong>? This action cannot be undone.
          </p>
        )}
      </Modal>
    </>
  );
};

export default RolesManagement;